import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { t } from '@lingui/core/macro';
import { UncontrolledTooltip } from 'src/components/ui';

import CodeMirror from '@uiw/react-codemirror';
import { keymap } from '@codemirror/view';
import { Prec } from '@codemirror/state';

import { json } from '@codemirror/lang-json';
import { lintGutter, forEachDiagnostic, setDiagnosticsEffect, diagnosticCount } from '@codemirror/lint';
import { vim } from '@replit/codemirror-vim';

import { debounce, isEmpty } from 'lodash';

import jsonLinter from './extensions/linter';
import BracketInserter from './extensions/brackets';

import ToolbarButton from './ToolbarButton';
import { VimLogo } from '../Icons/vimLogo';
import Icon from 'src/components/Icon';

const VIM_LOCAL_STORE_KEY = 'uwe-json-editor-use-vim';

// To determine platform specific keybinds
const isMac = navigator.platform?.toLowerCase().includes('mac');
const Mod = isMac ? 'Cmd' : 'Ctrl';

/**
 * A JSON editor component for viewing and editing JSON content, with optional
 * toolbar controls (save, reset, prettify, and custom buttons).
 *
 * Note that it is an uncontrolled component. The "value" prop merely sets the intial state on load.
 *
 * At the moment, the only diagnostics supported are error messages.
 *
 * @todo MAKE TOOLBAR STICKY!!!
 *
 * @todo Support yielding current object whenever onSave is called. This is not strictly necessary due to onChange.
 * @todo Only support keybinds if the component is actively focused... at the moment I think it is global, which might cause issues.
 * @todo Allow custom validation, so every component which uses this editor can enforce their own rules.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.value - The inital object to display in the editor.
 * @param {(value: Object) => void} [props.onChange] - Callback fired when the editor content changes.
 * @param {() => void} [props.onReset] - Callback fired when the reset button is clicked.
 * @param {() => void} [props.onSave] - Callback fired when the save button is clicked.
 *   Takes precedence over `onSaveForm` if both are provided.
 * @param {() => void} [props.onSaveForm] - (Deprecated) Legacy save handler.
 *   Retained for backwards compatibility; ignored if `onSave` is provided.
 * @param {string|number} [props.maxHeight] - Maximum height of the editor area (e.g. `'400px'` or `400`).
 * @param {Array<{color?: string, icon?: string, title?: string, onClick?: () => void, tooltip?: string}>} [props.buttons]
 *   - Optional custom toolbar buttons. Each entry defines a button’s appearance and behavior.
 * @param {boolean} [props.readOnly=false] - If true, the editor is read-only.
 * @param {boolean} [props.hideReset=false] - If true, hides the reset button.
 * @param {boolean} [props.hideSave=false] - If true, hides the save button.
 * @param {boolean} [props.hidePrettify=false] - If true, hides the prettify (format JSON) button.
 * @param {Array} [props.extraExtensions] - Additional CodeMirror extensions (custom linters,
 *   parsers, keymaps) appended after the built-ins. Must be a stable/memoised array —
 *   a new array identity reconfigures the editor.
 *
 * @returns {JSX.Element} The rendered JSON editor component.
 */
function JsonEditor(props) {
  const {
    id,
    value,
    onChange,
    onReset,
    onSave: propOnSave,
    onSaveForm: propOnSaveForm,
    maxHeight,
    buttons,
    readOnly,
    hideReset,
    hideSave,
    hidePrettify,
    extraExtensions,
  } = props;

  const onSave = useMemo(() => propOnSave || propOnSaveForm, [propOnSave, propOnSaveForm]);

  // ------------------------------
  // -- JSON state management
  // ------------------------------

  // The JSON string when editor loaded
  // This is utilized for resetting, but also to ensure correct initialization
  const [initialJsonString, setInitialJsonString] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (isInitialized) {
      return;
    }

    if (!isEmpty(value)) {
      const text = JSON.stringify(value, null, 4);
      setInitialJsonString(text);
      setIsInitialized(true);
    } else {
      setInitialJsonString('{}');
      setIsInitialized(true);
    }
  }, [value]);

  // Flags
  const [lastValidPretty, setLastValidPretty] = useState(new Date().getTime());
  const [errors, setErrors] = useState(null);
  // Tracks whether the document is malformed JSON (would make `JSON.parse` throw),
  // as opposed to merely having custom/business-rule linter diagnostics (see
  // `extraExtensions`). Prettify only needs the former to succeed, so it shouldn't
  // be blocked by the latter.
  const [hasSyntaxError, setHasSyntaxError] = useState(false);
  const lastNumOfErrors = useRef(0);

  // Ref for the editor, to modify it from toolbar or other buttons.
  // Using "editorRefCallback" workaround from:
  // https://github.com/uiwjs/react-codemirror/issues/314#issuecomment-1557816378
  const editorRef = useRef();
  const editorRefCallback = useCallback(
    editor => {
      if (!editorRef.current && editor?.editor && editor?.state && editor?.view) {
        editorRef.current = editor;
      }
    },
    [editorRef]
  );

  // Parser & propagator for the JSON representation:
  // - Parse the JSON
  // - Pass parsed object to onChange prop callback
  // In case of parse error, prints it to log.
  const propagateParsedJson = useCallback(
    value => {
      try {
        const json = JSON.parse(value);
        onChange?.(json);
        // console.log('DEBUG: propagated!');
      } catch (e) {
        const msg = e.toString();
        // console.log('JSON propagation (error): ', msg);
      }
    },
    [onChange]
  );

  // -----------------------------
  // -- Toolbar/keybind handlers
  // -----------------------------

  // Given the view, replace all contents with the prettified form of content.
  const handlePrettify = useCallback(() => {
    const view = editorRef.current?.view;
    if (!view) {
      throw new Error('JSON Editor: Could not find editorRef.current.view in `handlePrettify`');
    }

    const prettifiedDocString = JSON.stringify(JSON.parse(view.state.doc.toString()), null, 4);
    view.dispatch({
      changes: {
        from: 0,
        to: view.state.doc.toString().length,
        insert: prettifiedDocString,
      },
    });

    setLastValidPretty(new Date().getTime());
  }, [editorRef]);

  // Given the view, replace all contents with the original value prop; call onReset.
  const handleReset = useCallback(() => {
    const view = editorRef.current?.view;
    if (!view) {
      throw new Error('JSON Editor: Could not find editorRef.current.view in `handleReset`');
    }

    // BUGFIX: Prevent double propagation: The initial json is guaranteed error free anyway.
    lastNumOfErrors.current = 0;

    view.dispatch({
      changes: {
        from: 0,
        to: view.state.doc.toString().length,
        insert: initialJsonString,
      },
    });

    setLastValidPretty(new Date().getTime());
    onReset?.();
  }, [editorRef, onReset, initialJsonString, lastNumOfErrors]);

  // Given the view, save contents to local storage; call onSave
  const handleSave = useCallback(async () => {
    setIsInitialized(false);
    if (onSave) {
      await onSave();
    }
    setIsInitialized(true);
  }, [onSave, setIsInitialized]);

  // ---------------
  // -- Keybindings
  // ---------------
  // An alternative to the toolbar for calling the editor actions.

  // Maps certain key combinations to editor actions
  const keyMap = useMemo(
    () =>
      Prec.highest(
        keymap.of([
          { key: 'Mod-r', run: handleReset },
          { key: 'Mod-s', run: handleSave },
          { key: 'Mod-p', run: handlePrettify },
        ])
      ),
    [handleReset, handleSave, handlePrettify]
  );

  const [enableVimMode, setEnableVimMode] = useState(localStorage.getItem(VIM_LOCAL_STORE_KEY) === '1');

  const toggleVimMode = () => {
    if (enableVimMode) {
      setEnableVimMode(false);
      localStorage.setItem(VIM_LOCAL_STORE_KEY, '0');
    } else {
      setEnableVimMode(true);
      localStorage.setItem(VIM_LOCAL_STORE_KEY, '1');
    }
  };

  // Disables the default browser keybindings for keys used by the editor's custom keybindings!
  useEffect(() => {
    const handleKeyDown = e => {
      const { key, metaKey, ctrlKey } = e;

      if (((!isMac && ctrlKey) || (isMac && metaKey)) && (key === 'r' || key === 's' || key === 'p' || e.key === 'f')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // ------------
  // -- Toolbar
  // ------------
  const renderErrorIcon = () => {
    const warningId = (id || '') + '-error-icon';
    const errorListPrefix = (id || '') + '-error-list';
    return (
      <>
        <Icon icon="WarningCircle" id={warningId}
          
          style={{
            fontSize: '1.5rem',
            color: 'red',
            paddingTop: '4px',
            paddingRight: '5px',
            opacity: !!errors ? 1 : 0,
          }} />
        {id ? (
          <UncontrolledTooltip placement="top" target={warningId} fade={false}>
            <ul>
              {(errors || []).map((e, idx) => (
                <li key={errorListPrefix + idx}>{e}</li>
              ))}
            </ul>
          </UncontrolledTooltip>
        ) : null}
      </>
    );
  };

  const renderSaveButton = () => {
    if (!onSave) {
      return null;
    }

    return (
      <ToolbarButton
        className="btn btn-success"
        iconClassName="FloppyDisk"
        title={t`Save`}
        onClick={handleSave}
        disabled={!!errors}
        tooltip={`${Mod}-s`}
      />
    );
  };

  const renderResetButton = () => {
    return <ToolbarButton className="btn btn-dark" iconClassName="ArrowCounterClockwise" title={t`Reset`} onClick={handleReset} tooltip={`${Mod}-r`} />;
  };

  const renderPrettifyButton = () => {
    return (
      <ToolbarButton
        iconClassName="MagicWand"
        title="Prettify"
        onClick={handlePrettify}
        disabled={hasSyntaxError || lastValidPretty !== null}
        tooltip={`${Mod}-p`}
      />
    );
  };

  const renderExtraButtons = buttons => {
    return (buttons || []).map(({ color, icon, title, onClick, tooltip }, idx) => (
      <ToolbarButton key={idx} className={`btn btn-${color}`} iconClassName={icon} title={title} onClick={onClick} tooltip={tooltip} />
    ));
  };

  const renderVimButton = () => {
    return (
      <ToolbarButton
        className={!enableVimMode ? 'disabled-json-toolbar-btn' : ''}
        icon={<VimLogo fill="white" />}
        title="VIM"
        onClick={toggleVimMode}
        tooltip={`Toggle VIM Mode`}
      />
    );
  };

  const renderToolbar = () => {
    return (
      <div
        style={{
          margin: 'auto',
          maxWidth: '50%',
          display: 'flex',
          gap: '2px',
          flexDirection: 'row-reverse',
          alignItems: 'auto',
          marginRight: '0px',
        }}
      >
        {readOnly || hideReset ? null : renderResetButton()}
        {readOnly || hideSave ? null : renderSaveButton()}
        {!readOnly && renderVimButton()}
        {readOnly || hidePrettify ? null : renderPrettifyButton()}
        {renderExtraButtons(buttons)}
        {renderErrorIcon()}
      </div>
    );
  };

  // ---------------------
  // -- Editor callbacks
  // ---------------------

  // 1. Checks if linter found any diagnostics
  const onEditorUpdate = useCallback(
    debounce(viewUpdate => {
      const { state, transactions, docChanged, focusChanged, selectionSet } = viewUpdate;
      // console.log('onEditorUpdate will run', !docChanged && !focusChanged && !selectionSet, viewUpdate);

      // Skip document, focus, and selection updates
      if (docChanged || focusChanged || selectionSet) return;

      // Skip if linting changes were not observed
      const lintChanged = transactions.some(tr => tr.effects.some(e => e.is(setDiagnosticsEffect)));
      if (!lintChanged) return;

      // Gather all error diagnostics
      const errors = [];
      forEachDiagnostic(state, diag => {
        if (diag.severity === 'error') {
          const msg = `Editor Diagnostic error: ${diag.message}`;
          // console.log(msg);
          errors.push(msg);
        }
      });

      // Determine JSON validity directly, rather than from the aggregate
      // diagnostics above: those also include custom/business-rule linters
      // (see `extraExtensions`), which shouldn't gate prettifying since
      // prettify only needs `JSON.parse` to succeed.
      try {
        JSON.parse(state.doc.toString());
        setHasSyntaxError(false);
      } catch {
        setHasSyntaxError(true);
      }

      // If none of the diagnostics are errors AND we used to have errors, we propagate state
      if (errors.length === 0) {
        if (lastNumOfErrors.current > 0) {
          // console.log('Propagating because last number of errors was ', lastNumOfErrors.current);
          propagateParsedJson(state.doc.toString());
        }
        setErrors(null);
      } else {
        setErrors(errors);
      }

      // Keep track of the number of errors
      lastNumOfErrors.current = errors.length;
    }, 70),
    [propagateParsedJson, lastNumOfErrors]
  );

  // 2. Called when editor content is changed: propagate JSON representation upwards if no errors exist
  const onTextChange = useCallback(
    debounce((value, viewUpdate) => {
      const { docChanged, state } = viewUpdate;
      // console.log('onTextChange will run: ', docChanged, viewUpdate);

      // Skip if not a doc change:
      if (!docChanged) return;

      // Mark last pretty as stale if it has been more than 250 ms
      const now = new Date().getTime();
      setLastValidPretty(prev => (prev !== null && now - prev < 300 ? prev : null));

      // Since errors are tracked by linter instead of text changes,
      // we skip propagation if an error exists:
      if (diagnosticCount(state) > 0) return;

      // Propagate JSON
      propagateParsedJson(value);
    }, 250),
    [propagateParsedJson]
  );

  // ----------------
  // -- Editor props
  // ----------------
  // `extraExtensions` lets a caller add its own CodeMirror extensions —
  // additional linters above all. They go AFTER the built-ins so a caller's
  // linter composes with the JSON parse linter rather than replacing it, and
  // `lintGutter` (already mounted) renders both in the same gutter.
  //
  // Callers must memoise what they pass: a fresh array each render would
  // reconfigure the editor on every keystroke.
  const extensions = useMemo(() => [
    keyMap,
    json(),
    BracketInserter(),
    jsonLinter,
    lintGutter(),
    ...(enableVimMode ? [vim()] : []),
    ...(extraExtensions || []),
  ], [
    keyMap,
    enableVimMode,
    extraExtensions,
  ]);

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <div className="json-editor-toolbar">{renderToolbar()}</div>
      <div className="json-editor-container">
        {isInitialized ? (
          <CodeMirror
            ref={editorRefCallback}
            value={initialJsonString}
            width="100%"
            height="100%"
            theme="dark"
            basicSetup
            maxHeight={maxHeight}
            readOnly={readOnly}
            extensions={extensions}
            onChange={onTextChange}
            onUpdate={onEditorUpdate}
          />
        ) : null}
      </div>
    </div>
  );
}

export default JsonEditor;
